import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { CoachAnalysis } from '@/components/cr/CoachAnalysis';
import { coachAnalysisError } from '@/components/cr/coachError';
import { Paywall } from '@/components/cr/Paywall';
import type { AiUsage, CoachAnalysis as CoachAnalysisData } from '@/types/qbot';

interface CoachSlotProps {
  isPro: boolean;
  /** Only rendered once the user asks — the AI never fires on mount. */
  open: boolean;
  loading: boolean;
  error: unknown;
  analysis?: CoachAnalysisData;
  usage?: AiUsage;
}

/**
 * Where an AI answer lands, in place, under the deck that asked for it.
 *
 * Free tier reaches the paywall here rather than at the top of the panel: by
 * the time this renders, the deck, its rating and the import button have all
 * already been delivered for free.
 *
 * Nothing here auto-fires. The coach endpoints share a 10/h bucket per user
 * (`coach-deck` covers both `/coach/deck/:tag` and `/coach/deck-list`), so a
 * panel that analysed on mount would burn the hour's quota on a few section
 * swipes.
 */
export function CoachSlot({ isPro, open, loading, error, analysis, usage }: CoachSlotProps) {
  const { t } = useTranslation('cr');
  if (!open) return null;

  if (!isPro) {
    return (
      <div className="mt-3">
        <Paywall feature={t('coach.title')} />
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      {loading && (
        <div className="flex items-center gap-2 rounded-lg bg-bg-tertiary p-3 text-xs text-fg-muted">
          <Sparkles className="h-4 w-4 animate-pulse text-brand" />
          {t('analysis.thinking')}
        </div>
      )}
      {!!error && (
        <p className="rounded-lg bg-rose-500/10 p-3 text-xs text-rose-300">
          {coachAnalysisError(error, t)}
        </p>
      )}
      {analysis && <CoachAnalysis analysis={analysis} usage={usage} />}
    </div>
  );
}
